import type {
  DocumentType,
  MemberPayrollDetails,
  PaymentMethodType,
  Profile,
} from '@/types/database.types';
import type { Role } from '@/types/roles';

export type ProfileCompletionSectionId =
  | 'personal'
  | 'emergency'
  | 'work-payroll'
  | 'documents'
  | 'availability';

export type ProfileCompletionSource = 'profile' | 'payroll' | 'documents' | 'availability';

export interface ProfileCompletionIssue {
  id: string;
  label: string;
  section: ProfileCompletionSectionId;
  source: ProfileCompletionSource;
}

export interface RequiredProfileDocument {
  type: DocumentType;
  label: string;
}

export interface ProfileCompletionDocument {
  document_type: DocumentType | string;
  deleted_at?: string | null;
}

export interface ProfileCompletionInput {
  role: Role | null;
  profile?: Partial<Profile> | null;
  payroll?: Partial<MemberPayrollDetails> | null;
  documents?: ProfileCompletionDocument[];
  availabilityConfirmed?: boolean;
}

export interface ProfileCompletionSection {
  id: ProfileCompletionSectionId;
  required: boolean;
  complete: boolean;
  issues: ProfileCompletionIssue[];
}

export interface ProfileCompletionSummary {
  complete: boolean;
  issues: ProfileCompletionIssue[];
  sections: ProfileCompletionSection[];
  requiredDocuments: RequiredProfileDocument[];
  firstIncompleteSection: ProfileCompletionSectionId | null;
}

const PROFILE_SECTION_ORDER: ProfileCompletionSectionId[] = [
  'personal',
  'emergency',
  'work-payroll',
  'documents',
  'availability',
];

export function buildProfileCompletionSummary({
  role,
  profile,
  payroll,
  documents = [],
  availabilityConfirmed,
}: ProfileCompletionInput): ProfileCompletionSummary {
  const issues: ProfileCompletionIssue[] = [];
  const isOrganisationRole = role !== null && role !== 'ADMIN';

  addRequiredProfileValue(issues, profile?.full_name, 'full-name', 'Full name', 'personal');
  addRequiredProfileValue(issues, profile?.phone, 'mobile', 'Mobile', 'personal');
  addRequiredProfileValue(issues, profile?.dob, 'date-of-birth', 'Date of birth', 'personal');
  addRequiredProfileValue(
    issues,
    profile?.address_street,
    'street-address',
    'Street address',
    'personal',
  );
  addRequiredProfileValue(issues, profile?.address_suburb, 'suburb', 'Suburb', 'personal');
  addRequiredProfileValue(issues, profile?.address_state, 'state', 'State', 'personal');
  addRequiredProfileValue(issues, profile?.address_postcode, 'postcode', 'Postcode', 'personal');

  addRequiredProfileValue(
    issues,
    profile?.emergency_contact_name,
    'emergency-contact-name',
    'Emergency contact name',
    'emergency',
  );
  addRequiredProfileValue(
    issues,
    profile?.emergency_contact_relationship,
    'emergency-contact-relationship',
    'Emergency contact relationship',
    'emergency',
  );
  addRequiredProfileValue(
    issues,
    profile?.emergency_contact_phone,
    'emergency-contact-phone',
    'Emergency contact phone',
    'emergency',
  );

  if (isOrganisationRole) {
    addWorkEligibilityIssues(issues, profile);
    addPayrollIssues(issues, payroll);
  }

  const requiredDocuments = isOrganisationRole
    ? getRequiredProfileDocuments(role, profile?.residency_status, payroll?.payment_method_type)
    : [];
  const uploadedTypes = new Set(
    documents.filter((document) => !document.deleted_at).map((document) => document.document_type),
  );

  for (const document of requiredDocuments) {
    if (!uploadedTypes.has(document.type)) {
      issues.push({
        id: `document-${document.type.toLowerCase()}`,
        label: document.label,
        section: 'documents',
        source: 'documents',
      });
    }
  }

  if (role === 'EMPLOYEE' && !availabilityConfirmed) {
    issues.push({
      id: 'weekly-availability',
      label: 'Weekly availability',
      section: 'availability',
      source: 'availability',
    });
  }

  const requiredSections = new Set<ProfileCompletionSectionId>(['personal', 'emergency']);
  if (isOrganisationRole) {
    requiredSections.add('work-payroll');
    requiredSections.add('documents');
  }
  if (role === 'EMPLOYEE') requiredSections.add('availability');

  const sections = PROFILE_SECTION_ORDER.map((id) => {
    const sectionIssues = issues.filter((issue) => issue.section === id);
    const required = requiredSections.has(id);
    return {
      id,
      required,
      complete: !required || sectionIssues.length === 0,
      issues: sectionIssues,
    };
  });
  const firstIncompleteSection =
    sections.find((section) => section.required && !section.complete)?.id ?? null;

  return {
    complete: issues.length === 0,
    issues,
    sections,
    requiredDocuments,
    firstIncompleteSection,
  };
}

export function getRequiredProfileDocuments(
  role: Role | null,
  residencyStatus: string | null | undefined,
  paymentMethodType: PaymentMethodType | null | undefined,
): RequiredProfileDocument[] {
  if (!role || role === 'ADMIN') return [];

  const documents: RequiredProfileDocument[] = [
    { type: 'GOVERNMENT_ID', label: 'Government-issued photo ID' },
  ];
  const requiresVisa =
    residencyStatus === 'TEMPORARY_RESIDENT' || residencyStatus === 'WORKING_HOLIDAY';
  if (requiresVisa) documents.push({ type: 'VISA', label: 'Visa or work-right evidence' });

  if (role === 'BO') return documents;

  documents.push({ type: 'CONTRACT', label: 'Contract or engagement letter' });
  if (paymentMethodType === 'PAYG_EMPLOYEE') {
    documents.push({ type: 'TAX_FORM', label: 'Tax declaration evidence' });
  }
  if (paymentMethodType === 'ABN_CONTRACTOR' || paymentMethodType === 'ACN_CONTRACTOR') {
    documents.push({ type: 'BUSINESS_REGISTRATION', label: 'ABN or ACN registration evidence' });
  }

  return documents;
}

export function getSectionIssues(
  summary: ProfileCompletionSummary,
  section: ProfileCompletionSectionId,
) {
  return summary.sections.find((item) => item.id === section)?.issues ?? [];
}

function addWorkEligibilityIssues(
  issues: ProfileCompletionIssue[],
  profile: Partial<Profile> | null | undefined,
) {
  addRequiredProfileValue(
    issues,
    profile?.residency_status,
    'work-eligibility',
    'Work eligibility',
    'work-payroll',
  );

  if (
    profile?.residency_status === 'TEMPORARY_RESIDENT' ||
    profile?.residency_status === 'WORKING_HOLIDAY'
  ) {
    addRequiredProfileValue(
      issues,
      profile.visa_subclass,
      'visa-subclass',
      'Visa subclass',
      'work-payroll',
    );
    addRequiredProfileValue(
      issues,
      profile.visa_expiry_date,
      'visa-expiry',
      'Visa expiry date',
      'work-payroll',
    );
  }
}

function addPayrollIssues(
  issues: ProfileCompletionIssue[],
  payroll: Partial<MemberPayrollDetails> | null | undefined,
) {
  if (!payroll?.payment_method_type) {
    addIssue(issues, 'payment-arrangement', 'Payment arrangement', 'work-payroll', 'payroll');
    return;
  }

  const method = payroll.payment_method_type;
  if (method !== 'UNPAID') {
    addRequiredPayrollValue(issues, payroll.bank_bsb, 'bank-bsb', 'BSB');
    addRequiredPayrollValue(
      issues,
      payroll.bank_account_number,
      'bank-account-number',
      'Account number',
    );
    addRequiredPayrollValue(issues, payroll.bank_account_name, 'bank-account-name', 'Account name');
  }

  if (method === 'PAYG_EMPLOYEE') {
    if (!payroll.tfn_declaration_status || payroll.tfn_declaration_status === 'NOT_PROVIDED') {
      addIssue(issues, 'tfn-declaration', 'TFN declaration status', 'work-payroll', 'payroll');
    }
    if (payroll.tfn_declaration_status === 'PROVIDED') {
      addRequiredPayrollValue(issues, payroll.tfn, 'tfn', 'TFN');
    }
    addRequiredPayrollValue(issues, payroll.super_fund_name, 'super-fund', 'Super fund name');
    addRequiredPayrollValue(
      issues,
      payroll.super_member_number,
      'super-member-number',
      'Super member number',
    );
  }

  if (method === 'ABN_CONTRACTOR') {
    addRequiredPayrollValue(issues, payroll.abn, 'abn', 'ABN');
    addRequiredPayrollValue(
      issues,
      payroll.contractor_business_name,
      'trading-name',
      'Trading name',
    );
  }

  if (method === 'ACN_CONTRACTOR') {
    addRequiredPayrollValue(issues, payroll.acn, 'acn', 'ACN');
    addRequiredPayrollValue(
      issues,
      payroll.contractor_business_name,
      'company-name',
      'Company name',
    );
  }
}

function addRequiredProfileValue(
  issues: ProfileCompletionIssue[],
  value: unknown,
  id: string,
  label: string,
  section: ProfileCompletionSectionId,
) {
  if (!hasValue(value)) addIssue(issues, id, label, section, 'profile');
}

function addRequiredPayrollValue(
  issues: ProfileCompletionIssue[],
  value: unknown,
  id: string,
  label: string,
) {
  if (!hasValue(value)) addIssue(issues, id, label, 'work-payroll', 'payroll');
}

function addIssue(
  issues: ProfileCompletionIssue[],
  id: string,
  label: string,
  section: ProfileCompletionSectionId,
  source: ProfileCompletionSource,
) {
  issues.push({ id, label, section, source });
}

function hasValue(value: unknown) {
  return typeof value === 'string'
    ? value.trim().length > 0
    : value !== null && value !== undefined;
}
